import { useEffect } from 'react';
import {
  setAuthErrorHandler,
  setAuthRefreshHandler,
  setAuthRequestTokenProvider,
  setAuthTokenProvider,
} from '@/shared/data/client';
import { emitAuthDiagnostic } from './authDiagnostics';
import { getE2eAccessToken } from './sessionStorage';
import {
  isIdentityMismatchError,
  isMissingUserIdentityError,
  isUserNotFoundError,
  resolveTokenFromClerkClientSessions,
} from './useAuth.helpers';
import type { AuthSession } from './types';
import type { RefreshOutcome } from './useAuth.helpers';

type UseAuthClientBindingsArgs = {
  clerkClientRef: { current: unknown };
  clerkGetTokenRef: {
    current: (options?: { skipCache?: boolean }) => Promise<string | null>;
  };
  clearAuthRecoveryFailures: () => void;
  expireSession: (reason: 'timer' | 'refresh_error' | 'session_expired_event') => void;
  loading: boolean;
  runRefreshSessionRef: { current: (opts?: { force?: boolean }) => Promise<RefreshOutcome> };
  sessionRef: { current: AuthSession | null };
};

export const useAuthClientBindings = ({
  clerkClientRef,
  clerkGetTokenRef,
  clearAuthRecoveryFailures,
  expireSession,
  loading,
  runRefreshSessionRef,
  sessionRef,
}: UseAuthClientBindingsArgs): void => {
  useEffect(() => {
    setAuthTokenProvider(() => {
      const e2eToken = getE2eAccessToken();
      if (e2eToken) return e2eToken;
      if (loading) return null;
      return sessionRef.current?.tokens.accessToken ?? null;
    });
    setAuthRequestTokenProvider(async (input) => {
      const e2eToken = getE2eAccessToken();
      if (e2eToken) return e2eToken;
      if (loading) return null;
      const token =
        (await clerkGetTokenRef.current(input)) ??
        (await resolveTokenFromClerkClientSessions(clerkClientRef.current, input));
      if (token) {
        return token;
      }
      return sessionRef.current?.tokens.accessToken ?? null;
    });
    setAuthErrorHandler(async ({ error: authError, status }) => {
      const outcome = await runRefreshSessionRef.current({ force: true });
      if (outcome.didRefresh) {
        clearAuthRecoveryFailures();
        return true;
      }
      if (
        status === 401 &&
        (isIdentityMismatchError(authError) ||
          isMissingUserIdentityError(authError) ||
          isUserNotFoundError(authError))
      ) {
        emitAuthDiagnostic('auth_error_handler_forced_expire', {
          status,
          code: authError.code ?? null,
          message: authError.message,
          correlationId: authError.correlationId ?? null,
          errorId: authError.errorId ?? null,
          userId: sessionRef.current?.userId ?? null,
        });
        expireSession('refresh_error');
      }
      return false;
    });
    setAuthRefreshHandler(async (opts) => {
      const outcome = await runRefreshSessionRef.current({ force: opts?.force });
      return outcome.didRefresh;
    });
    return () => {
      setAuthRefreshHandler(undefined);
      setAuthErrorHandler(undefined);
      setAuthRequestTokenProvider(undefined);
      setAuthTokenProvider(undefined);
    };
  }, [
    clearAuthRecoveryFailures,
    clerkClientRef,
    clerkGetTokenRef,
    expireSession,
    loading,
    runRefreshSessionRef,
    sessionRef,
  ]);
};
